import { createContext, useContext } from "react";
import type { StaffingSettings } from "../../utils/models";

type UserSettingsContextType = {
	settings: StaffingSettings;
	updateSettings: (settings: Partial<StaffingSettings>, saveAsDefault?: boolean) => Promise<void>;
	/** True while a settings SAVE is in flight (the mutation) — not the initial load. See `settingsResolved`. */
	isLoading: boolean;
	/**
	 * False only while the very first user-settings fetch is still in flight. Callers that fire requests
	 * parameterized by a persisted setting should wait for this, otherwise they fetch once with
	 * `defaultSettings` and again with the real values — and paint the default's answer in between.
	 *
	 * Deliberately true (not false) when the settings query never runs at all — a non-head-nurse, whose
	 * query is disabled — so a gate built on this can never latch off permanently for that cohort. For
	 * them `defaultSettings` IS the resolved answer.
	 */
	settingsResolved: boolean;
};

export const UserSettingsContext = createContext<UserSettingsContextType | null>(null);

export const useUserSettingsContext = () => {
	const context = useContext(UserSettingsContext);

	if (!context) throw new Error("useUserSettingsContext must be used within a UserSettingsContextProvider");

	return context;
};
